import { Info, XCircle, CircleCheck, Trash2, Pencil, Star } from 'lucide-react';
import MuiAccordion from '@mui/material/Accordion';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import AutocompleteInput from 'components/Autocomplete/AutocompleteInput';
import Button from 'tailwind-components/Button/Button';
import { USER_TYPES } from 'constants/enum';
import { areAgencyAgentsLoaded } from 'helpers/agencyManagement/agency-agents-loaded';
import icons from 'constants/icons';
import { isDataEqual } from 'helpers/arrays';
import { getInitialValues } from 'hooks/useBobData';
import useSavedFilters from 'hooks/useSavedFilters';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import style from '../DashboardCustomerBob.module.scss';
import SaveFilterModal from './SaveFilterModal/SaveFilterModal';
import SavedFiltersHeader from './SavedFiltersHeader/SavedFiltersHeader';
import SimpleFilter from './SimpleFilter/SimpleFilter';

import {
  getFilterDataTooltipMessage,
  getStatusTooltipMessage
} from 'helpers/filters';

import {
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
  Tooltip
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import {
  getAppointmentsViewData,
  getSelectedAgenciesAgents,
  getSpecificAgencyData,
  getStatementsPeriods,
  showAlert
} from 'state/actions/actions';

import {
  handleDeleteFilter,
  handleFavorite,
  handleRenameFilter,
  handleUpdateFilterJSON
} from 'state/actions/filters';
import { getCompany } from 'helpers/getCompanyVariables';

const Accordion = styled(props => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0
  },
  '&::before': {
    display: 'none'
  },
  color: '#656464'
}));

const FilterIcon = icons['filterList'];
const ExpandMoreIcon = icons['arrowDropDown'];

const isLOA = obj => {
  return false;
  const compLevel =
    obj['Custom_Comp_Level'] !== null
      ? obj['Custom_Comp_Level']
      : obj['Comp_LevelsX'];
  return compLevel === 'LOA';
};

const company = getCompany();

const BobTableFilters = ({
  localLoader,
  filterCount,
  formData,
  clearFilters,
  handleChangeSelectValue,
  handleNetPremiumFilterChange,
  handleNetPremiumValueChange,
  handleEffectiveDateChange,
  applyFilters,
  setformData,
  showFilters,
  carriersOptions,
  setSelectedFilterData = () => {},
  hasUnsavedFilters = false,
  setSelectedFilterName,
  setFilterCount,
  riskManagement
}) => {
  const dispatch = useDispatch();
  const cancelTokenSource = axios.CancelToken.source();

  // Local states
  const [, setAgentsLoaded] = useState(false);
  const [allAgenciesAreLoa, setAllAgenciesAreLoa] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  const initialLoad = useRef(true);
  const savedFilters = useSelector(state => state.filters.list);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedFilterId, setSelectedFilterId] = useState(null);

  // Redux state
  const { zohoData } = useSelector(state => state.user);

  const currentAgenciesAgentsDict = useSelector(
    state => state.agencyManagement.comissions_tab_agents
  );

  const { agencyInformation } = useSelector(
    state => state.agencyManagement.agencyData
  );

  // Hooks
  const { loadingSavedFilters, isError: isErrorGetFilters } = useSavedFilters(
    zohoData.id,
    riskManagement ? 'risk-management' : 'bob'
  );

  const notAdminAndLOA = React.useMemo(
    () => zohoData.Position_at_Agency !== USER_TYPES.ADMIN && isLOA(zohoData),
    [zohoData]
  );

  const agenciesOptions = React.useMemo(() => {
    // If user is not admin, don't compute any of this
    if (zohoData.Position_at_Agency !== USER_TYPES.ADMIN) return [];

    // only a loaded agency has an id, and then a missing downline list means it has none
    const rawAgenciesOptions = agencyInformation.id
      ? (agencyInformation.Downline_Agencies_Three ?? []).concat([
          agencyInformation
        ])
      : undefined;

    const filteredAgenciesOptions = rawAgenciesOptions;
    if (
      rawAgenciesOptions?.length !== 0 &&
      filteredAgenciesOptions?.length === 0
    ) {
      setAllAgenciesAreLoa(true);
    }
    const returnAgenciesOptions =
      filteredAgenciesOptions?.map(agency => ({
        id: agency.id._value,
        name: agency.name._value
      })) || [];

    // Gets all the agents from all the agencies from the tree
    if (zohoData.Position_at_Agency === USER_TYPES.ADMIN) {
      const ids = [...returnAgenciesOptions?.map(agency => agency.id)];
      dispatch(
        getSelectedAgenciesAgents(
          () => {},
          currentAgenciesAgentsDict,
          ids,
          cancelTokenSource
        )
      );
    }

    return returnAgenciesOptions;
  }, [agencyInformation.id, agencyInformation.Downline_Agencies_Three]);

  const mainAgentsOptions = React.useMemo(() => {
    const mainOptions =
      zohoData.NPN !== null &&
      zohoData.NPN !== 0 &&
      zohoData.NPN !== '' &&
      zohoData.NPN !== '0' &&
      zohoData.NPN !== 1 &&
      zohoData.NPN !== '1' &&
      zohoData.NPN.toString().length > 4 &&
      zohoData.NPN.toString().length <= 9
        ? [
            {
              type: 'Main Agent',
              name: zohoData.First_Name + ' ' + zohoData.Last_Name,
              npn: zohoData.NPN,
              Agency_Name: 'Main Agent',
              Agency_Id: zohoData.Account_Name?.id,
              id: zohoData?.id,
              Position_at_Agency: zohoData.Position_at_Agency
            }
          ]
        : [];
    return mainOptions;
  }, [zohoData.NPN, zohoData.First_Name, zohoData.Last_Name, zohoData.id]);

  const agentsOptions = React.useMemo(() => {
    const selectedAgenciesAgents = Object.keys(currentAgenciesAgentsDict)
      .map(selected_agency_id => currentAgenciesAgentsDict[selected_agency_id])
      .filter(x => x)
      .flat()
      .filter(option => !isLOA(option))
      .map(option => {
        return {
          type: 'Downline Agent',
          name: option.First_Name + ' ' + option.Last_Name,
          npn: option.NPN,
          Agency_Id: option.Agency_Id,
          Agency_Name: option.Agency,
          id: option.id
        };
      })
      .filter(
        option =>
          option.npn !== null &&
          option.npn !== 0 &&
          option.npn !== '' &&
          option.npn !== '0' &&
          option.npn !== 1 &&
          option.npn !== '1' &&
          option.npn.toString().length > 4 &&
          option.npn.toString().length <= 9
      )
      .filter(option => option.npn !== zohoData.NPN);

    setAgentsLoaded(true);

    // If user is LOA, return only other agents
    if (isLOA(zohoData)) return selectedAgenciesAgents;

    // If user is not LOA, returns itself as well.
    return [...mainAgentsOptions, ...selectedAgenciesAgents];
  }, [currentAgenciesAgentsDict]);

  // Effects
  // Gets data from backend
  useEffect(() => {
    const getData = async () => {
      dispatch(
        getStatementsPeriods(
          () => {},
          () => {}
        )
      );
      dispatch(
        getAppointmentsViewData(
          () => {},
          () => {}
        )
      );
      if (zohoData.Position_at_Agency !== USER_TYPES.ADMIN) return;
      dispatch(
        getSpecificAgencyData(
          () => {},
          zohoData?.Account_Name?.id,
          () => {}
        )
      );
    };
    getData();
  }, []);

  // If all data from all sources has finished loading, turns off the local loader
  useEffect(() => {
    if (notAdminAndLOA)
      return () => {
        setAllLoaded(false);
      };
    const carriersOptionsAreLoaded = carriersOptions.length > 1;
    const agenciesOptionsAreLoaded =
      agenciesOptions.length > 0 ||
      allAgenciesAreLoa ||
      zohoData.Position_at_Agency !== USER_TYPES.ADMIN;
    const agentsOptionsAreLoaded = areAgencyAgentsLoaded({
      agentsOptions,
      isAdmin: zohoData.Position_at_Agency === USER_TYPES.ADMIN,
      agenciesOptions,
      agentsByAgency: currentAgenciesAgentsDict
    });

    if (
      carriersOptionsAreLoaded &&
      agentsOptionsAreLoaded &&
      agenciesOptionsAreLoaded &&
      !allLoaded
    ) {
      setAllLoaded(true);
    }
  }, [
    carriersOptions,
    agentsOptions,
    agenciesOptions,
    currentAgenciesAgentsDict
  ]);

  // Preselects main agent
  useEffect(() => {
    // Preselects the logged in agent if not LOA
    if (isLOA(zohoData)) {
      //nothing
    } else if (
      mainAgentsOptions.length > 0 &&
      allLoaded &&
      (mainAgentsOptions[0].Position_at_Agency !== 'Admin' ||
        (mainAgentsOptions[0].Position_at_Agency === 'Admin' &&
          agentsOptions.length > 1))
    ) {
      setformData(prevData => ({
        ...prevData,
        agents:
          mainAgentsOptions[0].Position_at_Agency === 'Admin'
            ? agentsOptions
            : mainAgentsOptions
      }));
    }
  }, [mainAgentsOptions, agentsOptions, allLoaded]);

  useEffect(() => {
    if (allLoaded && formData.agents.length > 0 && initialLoad.current) {
      applyFilters();
      initialLoad.current = false;
    }
  }, [formData.agents]);

  useEffect(() => {
    if (showFilters) setSelectedFilterData(formData);
  }, [showFilters]);

  useEffect(() => {
    if (selectedFilterId !== null) {
      applyFilters();
    }
  }, [selectedFilterId]);

  const onStartEditName = filter => {
    setEditingId(filter.id);
    setEditingName(filter.name);
  };

  const onFinishEditName = async filterId => {
    if (editingId == null || !filterId) return;

    if (editingName.trim() === '') {
      dispatch(
        showAlert({ message: 'Filter name must not be empty', code: 'error' })
      );
      return;
    }
    try {
      dispatch(
        handleRenameFilter(filterId, editingName, message => {
          dispatch(showAlert({ message, code: 'error' }));
        })
      );
    } catch (error) {}
    setEditingId(null);
    setEditingName('');
  };

  const onCancelEditName = () => {
    setEditingId(null);
    setEditingName('');
  };

  const selectedFilter = savedFilters.find(f => f.id === selectedFilterId);

  const hasUnsavedCustomFilter = useMemo(() => {
    if (!selectedFilter || !formData) return false;
    const parsedFilter =
      typeof selectedFilter.json === 'string'
        ? JSON.parse(selectedFilter.json)
        : selectedFilter.json || {};
    const agentsList =
      parsedFilter.agents === 'ALL AGENTS SELECTED'
        ? agentsOptions
        : Array.isArray(parsedFilter.agents)
        ? parsedFilter.agents
        : [];

    parsedFilter.agents = agentsList;
    return !isDataEqual(parsedFilter, formData);
  }, [formData, selectedFilter]);

  const invalidDateFilters = useMemo(() => {
    const startDate = formData.effectiveDate?.startDate;
    const endDate = formData.effectiveDate?.endDate;

    // Check if only one date is selected
    const onlyOneDateSelected =
      (startDate !== null && endDate === null) ||
      (startDate === null && endDate !== null);

    // Check if both dates are selected but endDate is before startDate
    const invalidDateRange =
      startDate !== null &&
      endDate !== null &&
      dayjs(endDate, 'MM-YYYY').isBefore(dayjs(startDate, 'MM-YYYY'), 'month');

    return onlyOneDateSelected || invalidDateRange;
  }, [formData.effectiveDate]);

  if (!showFilters) return null;

  return (
    <>
      <div className={style.filtersContainer}>
        <div className={style.filtersHeader}>
          <FilterIcon />
          <h4>
            {selectedFilterId
              ? `Selected Filter ("${selectedFilter.name}")`
              : `Filters (${filterCount})`}
          </h4>
        </div>
        <div className={style.filters}>
          <Grid
            container
            columnSpacing={{ xs: 0, sm: 2, lg: 0 }}
            rowSpacing={{ xs: 2, sm: 2, md: 3 }}
            style={{
              width: '85%',
              marginTop: '5px',
              margin: '5px 0px'
            }}
          >
            {!isErrorGetFilters && (
              <>
                <Grid
                  style={{ marginTop: 16, paddingTop: 0 }}
                  item
                  xs={12}
                  sm={6}
                  lg={12}
                >
                  <div>
                    <SavedFiltersHeader />
                    <div>
                      {loadingSavedFilters ? (
                        <CircularProgress size={12} />
                      ) : (
                        <List className={style.savedFiltersContainer}>
                          {savedFilters.map((filter, idx) => (
                            <Tooltip
                              key={filter.id}
                              title={getFilterDataTooltipMessage(
                                filter.name,
                                filter.description
                              )}
                              arrow
                              placement='right'
                            >
                              <ListItem
                                divider={idx < savedFilters.length - 1}
                                style={{
                                  cursor:
                                    selectedFilterId === filter.id
                                      ? 'default'
                                      : 'pointer'
                                }}
                                className={`${style.menuLink} ${
                                  selectedFilterId === filter.id
                                    ? style.active
                                    : undefined
                                }`}
                                onClick={() => {
                                  if (selectedFilterId === filter.id) return;
                                  setSelectedFilterId(filter.id);
                                  const savedJSON =
                                    typeof filter.json === 'string'
                                      ? JSON.parse(filter.json)
                                      : filter.json || {};
                                  const savedJSON_Keys = Object.keys(savedJSON);
                                  const VALID_FILTER_KEYS = Object.keys(
                                    getInitialValues(riskManagement)
                                  );

                                  let hasChanges = false;
                                  const normalizedSavedJSON = {};

                                  // adds missing keys with initial values or saves the existing ones
                                  for (const key of VALID_FILTER_KEYS) {
                                    if (key in savedJSON) {
                                      normalizedSavedJSON[key] = savedJSON[key];
                                    } else {
                                      normalizedSavedJSON[key] =
                                        getInitialValues(riskManagement)[key];
                                      hasChanges = true;
                                    }
                                  }

                                  // removes extra keys that are not in INITIAL_VALUES
                                  for (const key of savedJSON_Keys) {
                                    if (!VALID_FILTER_KEYS.includes(key)) {
                                      hasChanges = true;
                                    }
                                  }

                                  const agentsList =
                                    normalizedSavedJSON.agents ===
                                    'ALL AGENTS SELECTED'
                                      ? agentsOptions
                                      : Array.isArray(
                                          normalizedSavedJSON.agents
                                        )
                                      ? normalizedSavedJSON.agents
                                      : [];

                                  if (hasChanges) {
                                    dispatch(
                                      handleUpdateFilterJSON(
                                        filter.id,
                                        normalizedSavedJSON,
                                        message => {
                                          dispatch(
                                            showAlert({
                                              message,
                                              code: 'error'
                                            })
                                          );
                                        }
                                      )
                                    );
                                  }

                                  setformData({
                                    ...normalizedSavedJSON,
                                    agents: agentsList
                                  });

                                  const newCount = Object.keys(
                                    normalizedSavedJSON
                                  ).reduce((currentCount, key) => {
                                    const value = normalizedSavedJSON[key];

                                    // Skip netPremiumValue - we count netPremium as one filter
                                    if (key === 'netPremiumValue') {
                                      return currentCount;
                                    }

                                    // Count netPremium as one filter when both fields are set
                                    if (key === 'netPremiumFilter') {
                                      const hasFilter = value !== null;
                                      const hasValue =
                                        normalizedSavedJSON.netPremiumValue !==
                                        null;
                                      return hasFilter && hasValue
                                        ? currentCount + 1
                                        : currentCount;
                                    }

                                    // Count effectiveDate as one filter when both dates are set
                                    if (key === 'effectiveDate') {
                                      const hasStartDate =
                                        value?.startDate !== null &&
                                        value?.startDate !== undefined;
                                      const hasEndDate =
                                        value?.endDate !== null &&
                                        value?.endDate !== undefined;
                                      return hasStartDate && hasEndDate
                                        ? currentCount + 1
                                        : currentCount;
                                    }

                                    if (Array.isArray(value)) {
                                      return (
                                        currentCount +
                                        (value.length > 0 ? 1 : 0)
                                      );
                                    }

                                    if (typeof value === 'string') {
                                      return (
                                        currentCount +
                                        (value.trim() !== '' ? 1 : 0)
                                      );
                                    }

                                    if (typeof value === 'number') {
                                      return currentCount + 1;
                                    }

                                    return currentCount;
                                  }, 0);

                                  setFilterCount(newCount - 1);

                                  setSelectedFilterName(filter.name);

                                  setSelectedFilterData({
                                    ...normalizedSavedJSON,
                                    agents: agentsList
                                  });
                                }}
                              >
                                {editingId === filter.id ? (
                                  <TextField
                                    value={editingName}
                                    onChange={e =>
                                      setEditingName(e.target.value)
                                    }
                                    onKeyDown={e => {
                                      if (e.key === 'Enter')
                                        onFinishEditName(filter.id);
                                    }}
                                    autoFocus
                                    size='small'
                                    margin='dense'
                                    style={{ width: '75%' }}
                                    InputProps={{
                                      style: {
                                        fontSize: '0.875rem'
                                      }
                                    }}
                                    InputLabelProps={{
                                      style: { fontSize: '0.875rem' }
                                    }}
                                  />
                                ) : (
                                  <span>
                                    <ListItemText
                                      primary={filter.name}
                                      primaryTypographyProps={{
                                        cursor: 'pointer',
                                        fontSize: '0.875rem'
                                      }}
                                    />
                                  </span>
                                )}
                                <ListItemSecondaryAction>
                                  {editingId !== filter.id && (
                                    <IconButton
                                      edge='end'
                                      aria-label='favorite'
                                      size='small'
                                      onClick={() =>
                                        dispatch(
                                          handleFavorite(filter.id, message => {
                                            dispatch(
                                              showAlert({
                                                message,
                                                code: 'error'
                                              })
                                            );
                                          })
                                        )
                                      }
                                    >
                                      {filter.favorite ? (
                                        <Star
                                          size={20}
                                          fill='#FFBF00'
                                          style={{ color: '#FFBF00' }}
                                        />
                                      ) : (
                                        <Star size={20} />
                                      )}
                                    </IconButton>
                                  )}
                                  {editingId !== filter.id ? (
                                    <IconButton
                                      edge='end'
                                      aria-label='edit-name'
                                      size='small'
                                      onClick={() => onStartEditName(filter)}
                                    >
                                      <Pencil size={20} />
                                    </IconButton>
                                  ) : (
                                    <IconButton
                                      edge='end'
                                      aria-label='edit-name'
                                      size='small'
                                      onClick={() =>
                                        onFinishEditName(filter.id)
                                      }
                                    >
                                      <CircleCheck size={20} />
                                    </IconButton>
                                  )}
                                  {editingId !== filter.id ? (
                                    <IconButton
                                      edge='end'
                                      aria-label='delete'
                                      size='small'
                                      onClick={() =>
                                        dispatch(
                                          handleDeleteFilter(
                                            filter.id,
                                            message => {
                                              dispatch(
                                                showAlert({
                                                  message,
                                                  code: 'error'
                                                })
                                              );
                                            },
                                            () => {
                                              if (
                                                selectedFilterId === filter.id
                                              ) {
                                                setSelectedFilterId(null);
                                                setSelectedFilterName('');
                                                clearFilters();
                                                applyFilters(true);
                                              }
                                            }
                                          )
                                        )
                                      }
                                    >
                                      <Trash2 size={20} />
                                    </IconButton>
                                  ) : (
                                    <IconButton
                                      edge='end'
                                      aria-label='delete'
                                      size='small'
                                      onClick={onCancelEditName}
                                    >
                                      <XCircle size={20} />
                                    </IconButton>
                                  )}
                                </ListItemSecondaryAction>
                              </ListItem>
                            </Tooltip>
                          ))}
                          {savedFilters.length === 0 && (
                            <ListItem>
                              <ListItemText primary='No saved filters yet.' />
                            </ListItem>
                          )}
                        </List>
                      )}
                    </div>
                  </div>
                  <div
                    className={style.clearApplyFilters}
                    style={{ borderTop: '1px solid #e0e0e0' }}
                  >
                    {selectedFilterId && (
                      <Button
                        fill
                        size='medium'
                        variant='secondary'
                        text='Remove Filter'
                        onClick={() => {
                          clearFilters();
                          setSelectedFilterId(null);
                          applyFilters(true);
                          setSelectedFilterName('');
                        }}
                      />
                    )}
                    <Button
                      fill
                      size='medium'
                      variant='primaryDarkened'
                      isDisabled={
                        !hasUnsavedFilters &&
                        selectedFilterId &&
                        !hasUnsavedCustomFilter
                      }
                      text={`Save ${
                        selectedFilterId ? 'Changes' : 'New Filter'
                      }`}
                      onClick={() => {
                        setIsOpenModal(true);
                      }}
                    />
                  </div>
                </Grid>
              </>
            )}
            {zohoData.Position_at_Agency === USER_TYPES.ADMIN && (
              <>
                <Grid item xs={12} sm={6} lg={12}>
                  <AutocompleteInput
                    label={'agents'}
                    multiple
                    selectAll
                    loading={!allLoaded}
                    disabled={!allLoaded}
                    options={!allLoaded ? [] : agentsOptions}
                    isOptionEqualToValue={(option, value) =>
                      option.name === value.name
                    }
                    groupBy={option => option.Agency_Name}
                    getOptionLabel={option => option.name}
                    id='downlineagents'
                    value={!allLoaded ? [] : formData.agents}
                    onValueChanges={e => handleChangeSelectValue(e, 'agents')}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={'Agent(s)'}
                        placeholder={'Select agents'}
                        InputLabelProps={{
                          shrink: true
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {!allLoaded ? (
                                <CircularProgress color='inherit' size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    selectAllByGroup={true}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6} lg={12}>
              <Accordion slotProps={{ heading: { component: 'h4' } }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls='carrier-header'
                  id='carrier-header'
                >
                  Carrier
                </AccordionSummary>
                <AccordionDetails>
                  <AutocompleteInput
                    multiple
                    loading={localLoader}
                    options={carriersOptions
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .sort((a, b) => -b.type.localeCompare(a.type))}
                    isOptionEqualToValue={(option, value) =>
                      option.name === value.name
                    }
                    groupBy={option => option.type}
                    getOptionLabel={option => option.name}
                    id='carriers'
                    value={formData.carriers}
                    onValueChanges={e => handleChangeSelectValue(e, 'carriers')}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label='Carrier(s)'
                        placeholder={'Select carriers'}
                        InputLabelProps={{
                          shrink: true
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {localLoader ? (
                                <CircularProgress color='inherit' size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
            {!riskManagement && (
              <Grid item xs={12} sm={6} lg={12}>
                <Accordion slotProps={{ heading: { component: 'h4' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls='member-status-header'
                    id='member-status-header'
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <span>Member Status</span>
                      <Tooltip
                        title={getStatusTooltipMessage('Member')}
                        placement='right'
                        arrow
                      >
                        <Info
                          size={18}
                          style={{
                            color: '#d0d0d0',
                            cursor: 'default',
                            marginBottom: '4px'
                          }}
                        />
                      </Tooltip>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <SimpleFilter
                      options={['Active', 'Inactive', 'Pending', 'At Risk']}
                      value={formData.memberStatus}
                      handleChange={event => {
                        const {
                          target: { value }
                        } = event;

                        handleChangeSelectValue(value, 'memberStatus');
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}

            {company !== 'oneill' && (
              <Grid item xs={12} sm={6} lg={12}>
                <Accordion slotProps={{ heading: { component: 'h4' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls='bob-status-header'
                    id='bob-status-header'
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <span>BOB Status</span>
                      <Tooltip
                        title={getStatusTooltipMessage('BOB')}
                        placement='right'
                        arrow
                      >
                        <Info
                          fontSize='small'
                          style={{
                            color: '#d0d0d0',
                            cursor: 'default',
                            marginBottom: '4px'
                          }}
                        />
                      </Tooltip>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <SimpleFilter
                      options={['Dropped', 'On BOB']}
                      value={formData.fallenOff}
                      handleChange={event => {
                        const {
                          target: { value }
                        } = event;

                        handleChangeSelectValue(value, 'fallenOff');
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}

            <Grid item xs={12} sm={6} lg={12}>
              <Accordion slotProps={{ heading: { component: 'h4' } }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls='agent-status-header'
                  id='agent-status-header'
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <span>Agent Status</span>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <SimpleFilter
                    options={['Active', 'Inactive']}
                    value={formData.agentStatus}
                    handleChange={event => {
                      const {
                        target: { value }
                      } = event;

                      handleChangeSelectValue(value, 'agentStatus');
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item xs={12} sm={6} lg={12}>
              <Accordion slotProps={{ heading: { component: 'h4' } }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls='net-premium-header'
                  id='net-premium-header'
                >
                  <span>Net Premium</span>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        label='Filter Type'
                        value={formData.netPremiumFilter || ''}
                        onChange={event => {
                          handleNetPremiumFilterChange(event.target.value);
                        }}
                        InputLabelProps={{
                          shrink: true
                        }}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: selected =>
                            selected || 'Select filter type'
                        }}
                      >
                        <MenuItem value=''>
                          <em>None</em>
                        </MenuItem>
                        <MenuItem value='Net Premium >='>
                          Net Premium &gt;=
                        </MenuItem>
                        <MenuItem value='Net Premium <='>
                          Net Premium &lt;=
                        </MenuItem>
                        <MenuItem value='Net Premium ='>Net Premium =</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type='number'
                        label='Value'
                        value={formData.netPremiumValue}
                        onChange={event => {
                          handleNetPremiumValueChange(event.target.value);
                        }}
                        InputLabelProps={{
                          shrink: true
                        }}
                        inputProps={{
                          min: 0,
                          step: 1
                        }}
                        placeholder='Enter amount'
                        disabled={!formData.netPremiumFilter}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item xs={12} sm={6} lg={12}>
              <Accordion slotProps={{ heading: { component: 'h4' } }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls='effective-date-header'
                  id='effective-date-header'
                >
                  <span>Effective Date</span>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label='Start Date'
                          value={
                            formData.effectiveDate?.startDate
                              ? dayjs(
                                  formData.effectiveDate.startDate,
                                  'MM-YYYY'
                                )
                              : null
                          }
                          onChange={date => {
                            const formattedDate = date
                              ? date.format('MM-YYYY')
                              : null;
                            handleEffectiveDateChange(
                              'startDate',
                              formattedDate
                            );
                          }}
                          views={['year', 'month']}
                          openTo='month'
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              InputLabelProps: {
                                shrink: true
                              }
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label='End Date'
                          value={
                            formData.effectiveDate?.endDate
                              ? dayjs(formData.effectiveDate.endDate, 'MM-YYYY')
                              : null
                          }
                          onChange={date => {
                            const formattedDate = date
                              ? date.format('MM-YYYY')
                              : null;
                            handleEffectiveDateChange('endDate', formattedDate);
                          }}
                          views={['year', 'month']}
                          openTo='month'
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              InputLabelProps: {
                                shrink: true
                              }
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <div
              className={style.clearApplyFilters}
              style={{ flexDirection: 'column' }}
            >
              {Array.isArray(formData.agentStatus) &&
                formData.agentStatus.length === 0 && (
                  <FormHelperText
                    error
                    sx={{ mb: 0.5, fontSize: '0.75rem', width: '100%' }}
                  >
                    An Agent status must be selected
                  </FormHelperText>
                )}
              {invalidDateFilters && (
                <FormHelperText
                  error
                  sx={{ mb: 0.5, fontSize: '0.75rem', width: '100%' }}
                >
                  {formData.effectiveDate?.startDate !== null &&
                  formData.effectiveDate?.endDate !== null
                    ? 'End date must be after or equal to start date'
                    : 'Start date and end date must be defined'}
                </FormHelperText>
              )}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '12px',
                  justifyContent: 'center'
                }}
              >
                <Button
                  fill
                  text='Clear Filters'
                  size='medium'
                  variant='secondary'
                  isDisabled={filterCount === 0}
                  onClick={clearFilters}
                />
                <Button
                  fill
                  text='Apply Filters'
                  size='medium'
                  variant='primaryDarkened'
                  isDisabled={
                    !hasUnsavedFilters ||
                    (Array.isArray(formData.agentStatus) &&
                      formData.agentStatus.length === 0) ||
                    invalidDateFilters
                  }
                  onClick={() => {
                    applyFilters();
                    setSelectedFilterData(formData);
                  }}
                />
              </div>
            </div>
          </Grid>
        </div>
      </div>

      {isOpenModal && (
        <SaveFilterModal
          closeModal={() => setIsOpenModal(false)}
          filter={selectedFilterId ? selectedFilter : null}
          formData={formData}
          agentsOptions={agentsOptions}
          applyFilters={applyFilters}
          setSelectedFilterData={setSelectedFilterData}
        />
      )}
    </>
  );
};

BobTableFilters.propTypes = {
  localLoader: PropTypes.bool,
  filterCount: PropTypes.number,
  formData: PropTypes.object,
  clearFilters: PropTypes.func,
  handleChangeSelectValue: PropTypes.func,
  handleNetPremiumFilterChange: PropTypes.func,
  handleNetPremiumValueChange: PropTypes.func,
  handleEffectiveDateChange: PropTypes.func,
  applyFilters: PropTypes.func,
  setformData: PropTypes.func,
  showFilters: PropTypes.bool,
  carriersOptions: PropTypes.array,
  setSelectedFilterData: PropTypes.func,
  hasUnsavedFilters: PropTypes.bool,
  setSelectedFilterName: PropTypes.func.isRequired,
  setFilterCount: PropTypes.func.isRequired,
  riskManagement: PropTypes.bool
};

export default memo(BobTableFilters);
